import { Component, OnInit } from '@angular/core';
import { DetailsCardAction, DetailsCardContainerComponent } from '../../../shared/cards/details-card-container/details-card-container.component';
import { MatCardModule } from '@angular/material/card';
import { PageHeaderComponent } from '../../../shared/page-header/page-header.component';
import { BehaviorSubject, filter } from 'rxjs';
import { ModelTypeKeys } from '../../../../../enums/model-type-keys.enum';
import { FormSubmission } from '../../../../../models/entities/forms/form-submission';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ScrollService } from '../../../../../services/scroll.service';
import { GrowlerService } from '../../../../../services/growler.service';
import { finalize, map, switchMap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PropertyManagementCompanyService } from '../../../../../services/property-management-company/property-management-company.service';
import { PropertyManagementCompanyDataService } from '../../../../../services/property-management-company/property-manager-company-data.service';
import { FormSubmissionStatus } from '../../../../../models/entities/forms/form-submission-status';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatListModule } from '@angular/material/list';
import { CombineLinesListDirective, LoadingListDirective, StatusChipLineDirective } from '../../../shared/directives/list.directive';
import { NgForOf, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { LegfiDatePipeModule } from '../../../../../pipes/format/legfi-date.module';
import { AppStatusChipComponent } from '../../../shared/status-chip/status-chip.component';
import { StatusChangeSidenavComponent, StatusChangeSidenavInput, StatusChangeSidenavOutput } from '../../../shared/sidenav/status-change-sidenav/status-change-sidenav.component';
import { SidenavContentService } from '../../../shared/sidenav/sidenav-content/sidenav-content.service';
import { AvatarBlockModule } from '../../../shared/avatar-block/avatar-block.module';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AttachmentsCardComponent } from '../../../shared/cards/attachments-card/attachments-card.component';
import { CardWithTableContentDirective } from '../../../shared/cards/card.directive';
import { FormSubmissionApproval } from '../../../../../models/entities/forms/form-submission-approval';
import { RequestFormSubmissionApprovalsComponent } from '../request-form-submission-approvals/request-form-submission-approvals.component';
import { CommentsCardComponent } from '../../../shared/cards/comments-card/comments-card.component';
import { NotesCardComponent } from '../../../shared/cards/notes-card/notes-card.component';
import { MatDialogSizes } from '../../../../../enums/mat-dialog-sizes.enum';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { RequestFormService } from '../../../../../services/forms/request-form.service';
import { CsvExportService } from '../../../../../services/csv-export/csv-export.service';
import { OrganizationDataService } from '../../../../../services/organization/organization-data.service';
import { RequiredMarkerDirective } from '../../../shared/directives/form.directive';
import { ConfirmActionV2DialogComponent, ConfirmActionV2DialogInput } from '../../../shared/dialogs/confirm-action-dialog/confirm-action-dialog.component';
import { RouterButtonValue } from '../../../../../models/entities/router-button-value';
import { MemberAdminSidenavComponent, MemberAdminSidenavInput, MemberAdminSidenavOutput } from '../../../memberships/shared/member-admin-sidenav/member-admin-sidenav.component';
import { AddressLinePipe } from '../../../../../pipes/format/address-line.pipe';

@UntilDestroy()
@Component({
    selector: 'app-request-form-submission-details',
    templateUrl: './request-form-submission-details.component.html',
    standalone: true,
    imports: [
        DetailsCardContainerComponent,
        MatCardModule,
        PageHeaderComponent,
        MatProgressBarModule,
        MatListModule,
        LoadingListDirective,
        NgIf,
        LegfiDatePipeModule,
        AppStatusChipComponent,
        StatusChipLineDirective,
        NgForOf,
        NgSwitch,
        NgSwitchCase,
        NgSwitchDefault,
        AvatarBlockModule,
        ButtonDirective,
        MatButtonModule,
        MatIconModule,
        MatDialogModule,
        AttachmentsCardComponent,
        CardWithTableContentDirective,
        RequestFormSubmissionApprovalsComponent,
        CommentsCardComponent,
        NotesCardComponent,
        CombineLinesListDirective,
        RequiredMarkerDirective,
        RouterLink,
        AddressLinePipe,
    ],
})
export class RequestFormSubmissionDetailsComponent implements OnInit
{
    routerBackButton: RouterButtonValue;
    actions: DetailsCardAction[];
    lines = 1;
    loadingInitialData = true;

    approvalCounts = '-- / --';
    formSubmission: FormSubmission;
    formSubmissionStatus: FormSubmissionStatus;
    formSubmissionStatuses: FormSubmissionStatus[];

    readonly loadingSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    readonly modelType = ModelTypeKeys.FORM_SUBMISSION;

    constructor(
            private _propertyManagementCompanyService: PropertyManagementCompanyService,
            private _propertyManagementCompanyData: PropertyManagementCompanyDataService,
            private _organizationDataService: OrganizationDataService,
            private _requestFormService: RequestFormService,
            private _csvExportService: CsvExportService,
            private _activatedRoute: ActivatedRoute,
            private _router: Router,
            private _scrollService: ScrollService,
            private _sidenav: SidenavContentService,
            private _dialog: MatDialog,
            private _growler: GrowlerService,
    ) {
    }

    get pmcId()  {
        return this._propertyManagementCompanyData.pmcId;
    }

    ngOnInit() {
        this._scrollService.scrollToTop();
        this.lines = this.getDetailActions().length;

        this.routerBackButton = {
            label: 'Back',
            route: `/pmc/${this.pmcId}/requests/submissions`,
            queryParams: this._activatedRoute.snapshot.queryParams,
            ariaLabel: 'Go back to request form submissions',
        };

        this._activatedRoute.params.pipe(
                filter((params) => !!params.submissionId),
                map((params) => params.submissionId as number),
                switchMap((submissionId) => this.refreshFormSubmission(submissionId)),
                switchMap(() => {
                    return this._propertyManagementCompanyService.getPropertyManagementRequestFormSubmissionStatuses(
                            { organizationId: this.formSubmission?.organizationId }
                    );
                }),
                map((res) => this.formSubmissionStatuses = res.data !== null ? res.data : []),
                map(() => {
                    this.formSubmissionStatus = this.formSubmissionStatuses?.find((status) => {
                        return status.id === this.formSubmission.formSubmissionStatusId;
                    });
                }),
                untilDestroyed(this),
        ).subscribe({
            next: () => this.loadingInitialData = false,
            error: () => {
                this.loadingInitialData = false;
                this._growler.oops('Unable to load request form submission');
            },
        });
    }

    openStatusChange() {
        const data: StatusChangeSidenavInput = {
            modelType: 'submissions',
            model: this.formSubmission,
            statuses: this.formSubmissionStatuses.filter((s) => s.formId === this.formSubmission.formId),
        };

        this._sidenav.open(StatusChangeSidenavComponent, {
            data,
        }).afterClosed().subscribe((result: StatusChangeSidenavOutput) => {
            if (result?.success) {
                this.formSubmission.formSubmissionStatusId = result.status.id;
                this.formSubmission.status = result.status.title;
                this.formSubmissionStatus = result.status as FormSubmissionStatus;
            }
        });
    }

    updateApprovals(approvals: FormSubmissionApproval[]) {
        const approved = approvals.filter((approval) => approval.approves);
        this.approvalCounts = `${approved.length} / ${approvals.length}`;
    }

    private refreshFormSubmission(submissionId: number) {
        return this._propertyManagementCompanyService.getPropertyManagementRequestFormSubmission(submissionId).pipe(
                map((res) => {
                    this.formSubmission = res;
                    this.actions = this.getDetailActions();
                }),
        );
    }

    private toggleSubmissionStatus(isCompleting: boolean) {
        const data: ConfirmActionV2DialogInput = {
            title: isCompleting ? 'Close form submission?' : 'Re-open form submission?',
            content: isCompleting ? 'Mark request as closed for this form.' : 'Remove closed date and move to open requests for review.',
            confirmButton: isCompleting ? 'Yes, close this request' : 'Yes, re-open this request',
            cancelButton: 'No',
        };
        this._dialog.open(ConfirmActionV2DialogComponent, {
            panelClass: ['v2-dialog', 'confirmation-layout'],
            width: MatDialogSizes.SM,
            autoFocus: 'dialog',
            data,
        }).afterClosed().subscribe({
            next: (res) => {
                if (res?.success) {
                    this.loadingSubject$.next(true);
                    this._requestFormService.completeFormRequest(
                            this.formSubmission.id,
                            !!isCompleting,
                            this.formSubmission.organizationId,
                    ).pipe(
                            switchMap(() => this.refreshFormSubmission(this.formSubmission.id)),
                            finalize(() => this.loadingSubject$.next(false)),
                            untilDestroyed(this),
                    ).subscribe({
                        next: () => {
                            const message = 'Form submission ' + (isCompleting ? 'closed' : 're-opened');
                            this._growler.success('Success', message);
                        },
                        error: () => this._growler.oops('Unable to update form submission'),
                    });
                }
            },
        });
    }

    private generateFormSubmissionPdf() {
        this.loadingSubject$.next(true);
        this._requestFormService.getFormRequestPdfReport(this.formSubmission.id).pipe(
                finalize(() => this.loadingSubject$.next(false)),
                untilDestroyed(this),
        ).subscribe({
            next: (res) => {
                const filename = `payhoa-request-${this.formSubmission.id}.pdf`;
                this._csvExportService.downloadFile(res, filename, 'application/pdf');
            },
            error: () => this._growler.oops('Unable to generate file for form submission'),
        });
    }

    private editFormSubmission() {
        this.loadingSubject$.next(true);

        // TODO: new edit view for form submission, redirecting to old view for now
        const redirectTarget = `/app/form-submission/${this.formSubmission.id}/edit`;
        this._organizationDataService.switchOrganization(this.formSubmission.organizationId, {}, redirectTarget).pipe(
                finalize(() => this.loadingSubject$.next(false)),
                untilDestroyed(this),
        ).subscribe({
            next: ([url, isExternalRedirect]: [string, boolean]) => {
                if (isExternalRedirect) {
                    window.location.href = url;
                } else {
                    return this._router.navigateByUrl(url, {skipLocationChange: true});
                }
            },
            error: () => this._growler.oops('There was a problem switching current organization.'),
        });
    }

    private showNotificationMemberSelector() {
        const data: MemberAdminSidenavInput = {
            title: 'Send Notifications',
            required: true,
            requireEmail: true,
            orgId: this.formSubmission.organizationId,
            unitId: this.formSubmission.unitId,
            confirmButton: {
                '=0': 'Notify Recipients',
                '=1': 'Notify 1 Recipient',
                other: 'Notify # Recipients',
            },
            emptyStateLabel: 'No recipients so far...',
        };

        this._sidenav.open(MemberAdminSidenavComponent, {
            data,
            parentClass: 'selector-layout',
        }).afterClosed().subscribe((result: MemberAdminSidenavOutput) => {
            if (result?.success && result?.selectedMemberships.length > 0) {
                this.loadingSubject$.next(true);

                const requestBody = {
                    memberIds: result.selectedMemberships.map((m) => m.id),
                    emailOwners: false,
                    emailAdmins: false,
                    organizationId: this.formSubmission.form.organizationId,
                };
                this._requestFormService.sendFormRequestNotification(this.formSubmission.id, requestBody).pipe(
                        finalize(() => this.loadingSubject$.next(false)),
                        untilDestroyed(this),
                ).subscribe({
                    next: () => {
                        const count = result.selectedMemberships.length === 1 ? ' notification email' : (result.selectedMemberships.length + ' notification emails');
                        this._growler.success('Success', `Sent ${count}`);
                    },
                    error: () => this._growler.oops('Unable to send notifications'),
                });
            }
        });
    }

    private deleteFormSubmission(): void {
        const data: ConfirmActionV2DialogInput = {
            title: `Delete form submission?`,
            content: 'This action cannot be undone.',
            confirmButton: 'Yes, delete this request',
            cancelButton: 'No',
        };
        this._dialog.open(ConfirmActionV2DialogComponent, {
            panelClass: ['v2-dialog', 'confirmation-layout'],
            width: MatDialogSizes.SM,
            autoFocus: 'dialog',
            data,
        }).afterClosed().subscribe((res) => {
            if (res?.success) {
                this.loadingSubject$.next(true);
                this._requestFormService.deleteFormRequest(this.formSubmission.id, this.formSubmission.organizationId).pipe(
                        finalize(() => this.loadingSubject$.next(false)),
                        untilDestroyed(this),
                ).subscribe({
                    next: () => {
                        this._growler.success('Success', `Form submission deleted`);
                        return this._router.navigate([this.routerBackButton.route], {queryParams: this.routerBackButton.queryParams});
                    },
                    error: () => this._growler.oops('Unable to delete form submission'),
                });
            }
        });
    }

    private getDetailActions(): DetailsCardAction[] {
        return [
            this.formSubmission?.completionDate ? {
                icon: 'refresh',
                title:  'Re-open',
                action: 'reopen',
                callback: () => this.toggleSubmissionStatus(false),
            } : {
                svg: 'v2:fi-br-check',
                title:  'Close Submission',
                action: 'close',
                callback: () => this.toggleSubmissionStatus(true),
            },
            {
                svg: 'v2:fi-br-download',
                title: 'Download PDF',
                action: 'pdf',
                callback: () => this.generateFormSubmissionPdf(),
            },
            {
                svg: 'v2:fi-br-edit',
                title: 'Edit',
                action: 'edit',
                callback: () => this.editFormSubmission(),
            },
            {
                svg: 'v2:fi-br-paper-plane',
                title: 'Notify Others',
                action: 'notify',
                callback: () => this.showNotificationMemberSelector(),
            },
            {
                svg: 'v2:fi-br-trash',
                title: 'Delete',
                action: 'delete',
                callback: () => this.deleteFormSubmission(),
            },
        ];
    }
}
